import { useCallback } from 'react';

import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import {
  createConfirmTurnRequest,
  createMessageTurnRequest,
  createUndoTurnRequest,
} from '../store/aiBuilderTurnRequests';
import { isAiBuilderUsageBlocked } from '../store/aiBuilderUsagePolicy';
import {
  type AiBuilderChatState,
  type AiBuilderMessage,
  type AiBuilderSelectedQuizContext,
  type AiBuilderSubmitPromptOptions,
} from '../store/types';
import { type useDispatchServerTurn } from '../store/useAiBuilderServerTurnDispatcher';
import { aiBuilderChatAdapter } from '../utils/aiBuilderChatAdapter';
import { type AiBuilderChatCopy } from '../utils/aiBuilderChatCopy';
import { trackAiBuilderUsageBlocked } from '../utils/aiBuilderChatTracking';
import { type AiBuilderTurnRequest } from '../utils/types';

type AiBuilderUserMessage = Extract<AiBuilderMessage, { kind: 'user' }>;
type AiBuilderMutableRef<T> = { current: T };
type GetNextAiBuilderRequestId = () => number;
type IsAiBuilderTurnBusy = () => boolean;

type CreateTurnContextParams = {
  signal?: AbortSignal;
  selectedQuizContext?: AiBuilderSelectedQuizContext | null;
  sourceRefs?: string[];
  turnHints?: AiBuilderSubmitPromptOptions['turnHints'];
};

type UseAiBuilderUserMessageRetryParams = {
  beginUserMessageRetry: (messageId: string) => void;
  copy: AiBuilderChatCopy;
  createTurnContext: (
    params?: CreateTurnContextParams,
  ) => AiBuilderTurnRequest['context'];
  dispatchServerTurn: ReturnType<typeof useDispatchServerTurn>;
  isTurnBusy: IsAiBuilderTurnBusy;
  lastQuizListSnapshotRef: AiBuilderMutableRef<BuildQuizListItem[] | null>;
  getNextRequestId: GetNextAiBuilderRequestId;
  getChatState: () => AiBuilderChatState;
  getLatestMessages: () => AiBuilderChatState['messages'];
  retryQuestionSet: () => Promise<boolean>;
};

export const useAiBuilderUserMessageRetry = ({
  beginUserMessageRetry,
  copy,
  createTurnContext,
  dispatchServerTurn,
  getNextRequestId,
  isTurnBusy,
  lastQuizListSnapshotRef,
  getChatState,
  getLatestMessages,
  retryQuestionSet,
}: UseAiBuilderUserMessageRetryParams) => {
  return useCallback(
    (messageId: string) => {
      if (isTurnBusy()) return;

      const message = getLatestMessages().find(
        (candidate): candidate is AiBuilderUserMessage =>
          candidate.kind === 'user' && candidate.id === messageId,
      );

      if (!message || message.status !== 'failed' || !message.retry) return;

      const retry = message.retry;
      if (retry.type === 'questionSet') {
        void retryQuestionSet();
        return;
      }

      const usage = getChatState().usage;
      if (isAiBuilderUsageBlocked(usage)) {
        trackAiBuilderUsageBlocked(usage, 'retry');
        return;
      }

      const requestId = getNextRequestId();
      beginUserMessageRetry(messageId);
      lastQuizListSnapshotRef.current = null;

      if (retry.type === 'message') {
        dispatchServerTurn(
          aiBuilderChatAdapter.startTurn,
          signal =>
            createMessageTurnRequest({
              requestId,
              clientRequestId: retry.clientRequestId,
              clientCopy: copy,
              message: retry.prompt,
              context: createTurnContext({
                signal,
                selectedQuizContext: retry.selectedQuizContext,
                sourceRefs: retry.sourceRefs,
                ...(retry.turnHints ? { turnHints: retry.turnHints } : {}),
              }),
            }),
          { userMessageId: messageId },
        );
        return;
      }

      if (retry.type === 'confirm') {
        dispatchServerTurn(
          aiBuilderChatAdapter.startTurn,
          signal =>
            createConfirmTurnRequest({
              requestId,
              clientRequestId: retry.clientRequestId,
              clientCopy: copy,
              token: retry.token,
              decision: retry.decision,
              context: createTurnContext({ signal }),
            }),
          { userMessageId: messageId },
        );
        return;
      }

      dispatchServerTurn(
        aiBuilderChatAdapter.startTurn,
        signal =>
          createUndoTurnRequest({
            requestId,
            clientRequestId: retry.clientRequestId,
            clientCopy: copy,
            context: createTurnContext({ signal }),
          }),
        { userMessageId: messageId },
      );
    },
    [
      beginUserMessageRetry,
      copy,
      createTurnContext,
      dispatchServerTurn,
      getChatState,
      getNextRequestId,
      getLatestMessages,
      isTurnBusy,
      lastQuizListSnapshotRef,
      retryQuestionSet,
    ],
  );
};
